/**
 * HVAC Design — the inputs, all of them, from the screen.
 *
 * KAPL, 27-Sep-2026: "I want the user actually enter the values from the UI
 * itself … make all the required placeholders mentioned in the excel sheet, all
 * the input should be provided from UI. Also, based on location for indoor and
 * outdoor condition the dry bulb, wet bulb, RH values should be taken from
 * internet."
 *
 * The sheet is KAPL_IIT_BHU_Roomwise_Heat_Load_CFM_Developer_Spec.xlsx:
 *   01_GLOBAL_INPUTS  → DesignBasisPanel (location, weather, every global input)
 *   02_ROOM_INPUTS    → RoomInputsGrid (every room column, edited in place)
 *
 * Nothing here calculates a load. The weather is derived on the server
 * (climate_service) and every psychrometric figure shown beside an input comes
 * back from the server's own engine, so the screen and the heat load cannot
 * disagree about what 32.7 °C at 72 % means.
 */

import React, { useState } from "react";
import { useRanges, bandFor, RangeInput, saveWithReason, outside } from "./RangeField";
import { useInputScope, ScopeMark, ScopeNote, HiddenValues } from "./inputScope";

const KZ = { blue: "#1F4E79", green: "#10B981", amber: "#F59E0B",
             red: "#DC2626", charcoal: "#111827", grey: "#6B7280" };
const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
              textTransform: "uppercase", color: "#9CA3AF", display: "block",
              marginBottom: 4 };
const inp = { width: "100%", boxSizing: "border-box", padding: "7px 10px",
              fontSize: 13, border: "1px solid #E5E7EB", borderRadius: 7,
              fontFamily: "inherit" };
const inpLocked = { ...inp, background: "#F3F4F6", color: "#6B7280",
                    borderStyle: "dashed", cursor: "not-allowed" };
const th = { padding: "6px 8px", fontWeight: 700, fontSize: 11, color: "#6B7280",
             borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" };
const td = { padding: "5px 8px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6",
             fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const fmt = (v, d = 1) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(d);

function Section({ title, sub, children, right }) {
  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10,
                    flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: KZ.charcoal }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: KZ.grey }}>{sub}</div>}
        <div style={{ flex: 1 }} />
        {right}
      </div>
      {children}
    </div>
  );
}

function Badge({ tone, children }) {
  const c = { green: ["#F0FDF4", "#166534", "#BBF7D0"], amber: ["#FFFBEB", "#92400E", "#FDE68A"],
              grey: ["#F3F4F6", "#4B5563", "#E5E7EB"], blue: ["#EFF6FF", "#1E40AF", "#BFDBFE"] }[tone];
  return (
    <span style={{ background: c[0], color: c[1], border: `1px solid ${c[2]}`, borderRadius: 999,
                   padding: "2px 9px", fontSize: 11.5, fontWeight: 600 }}>{children}</span>
  );
}

/** A table of psychrometric states — the same shape for proposed and applied.
 *  `tierOf` (VRF projects, KAPL A2 / C3) adds each case's source: a case equal
 *  to the weather lookup is a cross-check only; a stated one names its tier. */
function StatesTable({ cases, editable, onEdit, tierOf }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>{["Design case", ...(tierOf ? ["Source (VRF priority)"] : []), "Use", "Dry bulb °C", "Wet bulb °C", "RH %",
                 "Dew point °C", "Moisture g/kg", "Enthalpy kJ/kg"].map(h => <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {cases.map((c, i) => (
            <tr key={c.key || c.label || i}>
              <td style={{ ...td, fontWeight: 600 }}>{c.label}</td>
              {tierOf && <td style={td}>{editable && (c.use || "cooling") === "cooling"
                ? <select value={c.source_tier || ""} onChange={e => onEdit(i, "source_tier", e.target.value || null)}
                    style={{ ...inp, width: 200, padding: "3px 6px" }}>
                    <option value="">{tierOf(c) === "LOOKUP_COMPARISON" ? TIER_LABEL.LOOKUP_COMPARISON : "— state the source —"}</option>
                    {WEATHER_TIERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                : (TIER_LABEL[tierOf(c)] || "— source not stated —")}</td>}
              <td style={{ ...td, color: KZ.grey }}>{c.use || "cooling"}</td>
              <td style={td}>
                {editable ? (
                  <input type="number" step="0.1" defaultValue={c.db_c}
                    key={`db-${i}-${c.db_c}`}
                    onBlur={e => e.target.value !== String(c.db_c) && onEdit(i, "db_c", e.target.value)}
                    style={{ ...inp, width: 80, padding: "3px 6px" }} />
                ) : fmt(c.db_c)}
              </td>
              <td style={td}>{fmt(c.wb_c)}</td>
              <td style={td}>
                {editable ? (
                  <input type="number" step="0.1" defaultValue={c.rh != null ? +(c.rh * 100).toFixed(1) : ""}
                    key={`rh-${i}-${c.rh}`}
                    onBlur={e => {
                      const v = e.target.value;
                      if (v !== "" && Math.abs(Number(v) / 100 - (c.rh || 0)) > 1e-6) onEdit(i, "rh", v);
                    }}
                    style={{ ...inp, width: 72, padding: "3px 6px" }} />
                ) : fmt(c.rh != null ? c.rh * 100 : null)}
              </td>
              <td style={td}>{fmt(c.dew_point_c)}</td>
              <td style={td}>{fmt(c.w_g_per_kg, 2)}</td>
              <td style={td}>{fmt(c.enthalpy_kj_kg)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The spec's 01_GLOBAL_INPUTS, grouped as an engineer reads them. */
const GLOBAL_GROUPS = [
  ["Site", [
    ["altitude_m", "Altitude", "m", "Sets barometric pressure, and with it the latent load"],
  ]],
  ["Air properties", [
    ["air_density", "Air density", "kg/m³", "Derived per room from its own conditions at the site pressure — type a value only to override"],
    ["air_cp", "Air specific heat (Cp)", "kJ/kg·K"],
  ]],
  ["Supply air & chilled water", [
    ["supply_air_c", "Supply air temperature", "°C", "Required for load-based CFM"],
    ["chw_entering_c", "CHW entering", "°C"],
    ["chw_leaving_c", "CHW leaving", "°C"],
  ]],
  ["Internal loads", [
    ["person_sensible_w", "Person sensible heat", "W/person"],
    ["person_latent_w", "Person latent heat", "W/person"],
    ["lighting_w_per_m2", "Lighting heat density", "W/m²", "Replace with actual lighting watts"],
  ]],
  ["Envelope", [
    ["heat_load_method", "Heat load method", "QUICK / DETAILED",
     "QUICK uses the W/m² allowance; DETAILED uses each room's walls, roof, glass and floor"],
    ["envelope_w_per_m2", "Quick envelope allowance", "W/m²", "Used only in QUICK"],
    ["envelope_include_roof_floor", "Roof & floor in envelope", "override",
     "KAPL rule: sun-exposed wall and glass only — roof/floor only by an approved project override"],
    ["ground_temp_c", "Ground / adjacent floor temperature", "°C", "DETAILED floor conduction"],
  ]],
  ["HEPA / filtration", [
    ["hepa_length_mm", "HEPA module length", "mm", "Blank = the default terminal module"],
    ["hepa_width_mm", "HEPA module width", "mm"],
    ["hepa_face_velocity_ms", "HEPA face velocity", "m/s", "Where a room states none"],
    ["hepa_airflow_mode", "HEPA airflow mode", "A / B",
     "B enforces the face velocity: supply = MAX(Base, filter area × velocity). A: coverage is layout only"],
  ]],
  ["Reserves & allowances", [
    ["cooling_reserve", "Cooling capacity reserve", "fraction", "Applied at the AHU summary only — 0.1 = 10%"],
    ["airflow_reserve", "Airflow reserve", "fraction", "Applied at the AHU summary only"],
    ["safety_factor", "Terminal-unit sizing margin", "fraction",
     "DX / VRF / split indoor units only. Cleanroom loads carry no hidden margin (HL-001 §10.1)"],
    ["fresh_air_pct", "Fresh air (where a room states none)", "%"],
  ]],
  ["Air distribution", [
    ["duct_velocity_main_ms", "Duct velocity, main", "m/s"],
    ["duct_velocity_branch_ms", "Duct velocity, branch", "m/s"],
    ["fan_efficiency", "Fan efficiency", "fraction"],
    ["motor_efficiency", "Motor efficiency", "fraction"],
  ]],
];

export function DesignBasisPanel({ api, authFetch, projectId, basis, editable, lockedWhy,
                                   onReload, setErr, setBusy }) {
  const [edit, setEdit] = useState({});
  const [q, setQ] = useState("");
  const [places, setPlaces] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [stations, setStations] = useState(null);
  const [looking, setLooking] = useState("");
  const ranges = useRanges(api, authFetch);
  // The project's input matrix decides which global inputs it is asked for
  // (KAPL ENG VRF IN 001 on a VRV / VRF project); none → all, as before.
  const sc = useInputScope(api, authFetch, projectId);
  const B = (k) => sc.show("basis", k);
  const groups = GLOBAL_GROUPS.map(([g, fields]) => [g, fields.filter(([k]) => B(k))])
    .filter(([, fields]) => fields.length);

  const put = async (body, what) => {
    setBusy(what || "Saving the design basis…"); setErr("");
    try {
      // A value outside its allowed range asks for the reason and goes in as
      // an override for the project's checker / approver (parameter ranges).
      await saveWithReason(reason => authFetch(`${api}/api/eng/projects/${projectId}/design-conditions`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reason ? { ...body, override_reason: reason } : body),
      }));
      setEdit({});
      await onReload();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  const search = async () => {
    if (q.trim().length < 2) return;
    setLooking("Searching…"); setPlaces(null); setProposal(null); setStations(null); setErr("");
    try {
      const r = await authFetch(`${api}/api/eng/climate/locations?q=${encodeURIComponent(q.trim())}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Location search failed");
      setPlaces(d.results || []);
    } catch (e) { setErr(String(e.message || e)); }
    setLooking("");
  };

  const pick = async (p) => {
    setLooking(`Reading the hourly weather for ${p.label}…`); setErr("");
    try {
      const r = await authFetch(`${api}/api/eng/climate/design-conditions?latitude=${p.latitude}`
        + `&longitude=${p.longitude}${p.elevation_m != null ? `&elevation_m=${p.elevation_m}` : ""}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Weather data unavailable");
      setProposal({ place: p, ...d });
      setPlaces(null);
      // KAPL 26-Oct-2026: the station route — the system proposes the nearest
      // NOAA ISD stations; the engineer picks one, the Checker confirms it.
      authFetch(`${api}/api/eng/climate/stations?latitude=${p.latitude}&longitude=${p.longitude}`)
        .then(r => r.ok ? r.json() : null).then(s => setStations(s ? { ...s, place: p } : null))
        .catch(() => setStations(null));
    } catch (e) { setErr(String(e.message || e)); }
    setLooking("");
  };

  const fromStation = async (st) => {
    const p = stations.place;
    setLooking(`Reading ten years of ${st.name} station records…`); setErr("");
    try {
      const r = await authFetch(`${api}/api/eng/climate/station-design-conditions?station_id=${st.id}`
        + `&latitude=${p.latitude}&longitude=${p.longitude}${p.elevation_m != null ? `&elevation_m=${p.elevation_m}` : ""}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Station records unavailable");
      setProposal({ place: p, ...d });
    } catch (e) { setErr(String(e.message || e)); }
    setLooking("");
  };

  const apply = () => {
    const p = proposal;
    put({
      location_name: p.place.label, latitude: p.place.latitude, longitude: p.place.longitude,
      altitude_m: p.elevation_m,
      climate: { ...p, place: p.place },
      // origin marks the lookup's cases: on a VRF project they stay a
      // cross-check (KAPL C3) even when edited, until a source is stated.
      outdoor_cases: p.cases.map(c => ({ key: c.key, label: c.label, use: c.use,
                                         db_c: c.db_c, rh: c.rh, origin: "WEATHER_LOOKUP" })),
      source: `${p.place.label} — ${p.source}`,
    }, "Applying the location's design conditions…").then(() => { setProposal(null); setStations(null); });
  };

  const approve = async () => {
    setBusy("Approving the weather basis…"); setErr("");
    try {
      const r = await authFetch(`${api}/api/eng/projects/${projectId}/design-conditions/approve-weather`,
        { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Could not approve");
      await onReload();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };

  const editCase = (i, field, raw) => {
    const cases = (basis.outdoor_cases || []).map(c => ({ ...c }));
    if (field === "source_tier") {
      if (raw) cases[i].source_tier = raw; else delete cases[i].source_tier;
      put({ outdoor_cases: cases }, "Saving the case's source…");
      return;
    }
    const v = Number(raw);
    if (!Number.isFinite(v)) { setErr("That needs to be a number."); return; }
    cases[i][field] = field === "rh" ? v / 100 : v;
    put({ outdoor_cases: cases }, "Saving the outdoor case…");
  };
  // VRF (KAPL A2 / C3): a design-basis case equal to a looked-up case is the
  // lookup's — a cross-check only, never the governing value.
  const looked = (basis.climate && basis.climate.cases) || [];
  const tierOf = sc.applies ? (c => c.source_tier || ((c.origin === "WEATHER_LOOKUP"
    || looked.some(l => Math.abs(l.db_c - c.db_c) < 1e-9 && Math.abs(l.rh - c.rh) < 1e-9)) ? "LOOKUP_COMPARISON" : null)) : null;

  const status = basis.climate_status;
  const applied = basis.outdoor_cases_state || [];
  const val = (k) => (edit[k] !== undefined ? edit[k] : (basis[k] ?? ""));
  const stated = new Set(basis.stated || []);

  const saveGlobals = () => {
    const body = {};
    for (const [k, v] of Object.entries(edit)) {
      if (k === "heat_load_method" || k === "source" || k === "hepa_airflow_mode") body[k] = v;
      else if (k === "envelope_include_roof_floor") body[k] = v === "yes";
      else if (k === "indoor_rh") body[k] = v === "" ? null : Number(v) / 100;
      else body[k] = v === "" ? null : Number(v);
    }
    put(body);
  };

  return (
    <>
      <ScopeNote sc={sc}>On this screen: the site, its paired outdoor cases with their source, their approval and the
        altitude. Indoor DB / RH are per room; the design weather and constructions are the heat balance inputs
        below; the constants are in Design Constants.</ScopeNote>
      <HiddenValues sc={sc} />
      {/* ── Location & outdoor design conditions ─────────────────────────── */}
      <Section title={<>Location & outdoor design conditions<ScopeMark sc={sc} rec="basis" k="outdoor_cases" /></>}
        sub="Dry bulb, wet bulb and RH for the site — calculated from hourly weather records"
        right={status === "APPROVED"
          ? <Badge tone="green">Approved{basis.climate_approved_by ? ` by ${basis.climate_approved_by}` : ""}</Badge>
          : status === "FETCHED" ? <Badge tone="amber">Looked up — awaiting approval</Badge>
          : applied.length ? <Badge tone="blue">Stated by hand</Badge>
          : <Badge tone="grey">{sc.applies ? "Not stated" : "Budgetary default (38 °C / 50%)"}</Badge>}>

        {basis.location_name && (
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            <b>{basis.location_name}</b>
            <span style={{ color: KZ.grey }}>
              {basis.latitude != null && ` · ${fmt(basis.latitude, 3)}°, ${fmt(basis.longitude, 3)}°`}
              {basis.altitude_m != null && ` · ${fmt(basis.altitude_m, 0)} m`}
              {basis.atm_pressure_kpa != null && ` · ${fmt(basis.atm_pressure_kpa, 2)} kPa`}
            </span>
          </div>
        )}

        {editable && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <input value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()}
              placeholder="Project location — e.g. Varanasi, Pune, Hosur…"
              style={{ ...inp, maxWidth: 380 }} />
            <button onClick={search} disabled={q.trim().length < 2 || !!looking}
              style={{ padding: "7px 16px", background: KZ.blue, color: "#fff", border: 0,
                       borderRadius: 7, fontWeight: 600, cursor: "pointer" }}>
              Look up weather
            </button>
            {looking && <span style={{ fontSize: 12.5, color: KZ.grey, alignSelf: "center" }}>{looking}</span>}
          </div>
        )}

        {places && (
          <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, marginBottom: 10 }}>
            {places.length === 0 && (
              <div style={{ padding: 10, fontSize: 12.5, color: KZ.grey }}>No place by that name.</div>
            )}
            {places.map((p, i) => (
              <div key={i} onClick={() => pick(p)}
                style={{ padding: "8px 11px", cursor: "pointer", fontSize: 13,
                         borderTop: i ? "1px solid #F3F4F6" : 0 }}>
                <b>{p.label}</b>
                <span style={{ color: KZ.grey }}> · {fmt(p.latitude, 3)}°, {fmt(p.longitude, 3)}°
                  {p.elevation_m != null && ` · ${fmt(p.elevation_m, 0)} m`}</span>
              </div>
            ))}
            <div style={{ padding: "5px 11px", fontSize: 11, color: KZ.grey, borderTop: "1px solid #F3F4F6" }}>
              © OpenStreetMap contributors · elevation SRTM 30 m (OpenTopoData)
            </div>
          </div>
        )}

        {proposal && (
          <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8,
                        padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Proposed for {proposal.place.label} — {proposal.period},
              {" "}{(proposal.hours || 0).toLocaleString("en-IN")} {proposal.dataset === "NOAA_ISD" ? "observed hours"
                : proposal.dataset === "ERA5_LAND" ? "ERA5-Land hours" : "hours"}
            </div>
            {proposal.dataset === "NOAA_ISD" && proposal.station && (
              <div style={{ fontSize: 12.5, marginBottom: 6 }}>
                <b>{proposal.label}</b> — station {proposal.station.name} (ISD {proposal.station.id}),
                {" "}{fmt(proposal.station.distance_km, 1)} km from the site, {fmt(proposal.station.elevation_m, 0)} m;
                {" "}{(proposal.years_used || []).length} of {(proposal.years || []).length} years complete
                ({proposal.reports_per_day} reports a day). Not published ASHRAE values.
              </div>
            )}
            <StatesTable cases={proposal.cases} />
            <div style={{ fontSize: 11.5, color: KZ.grey, marginTop: 8 }}>
              {proposal.source}. Hottest hour {fmt(proposal.extremes?.max_db_c)} °C,
              coldest {fmt(proposal.extremes?.min_db_c)} °C. {proposal.caveat}
              {proposal.attribution && <> {proposal.attribution}</>}
            </div>
            {proposal.comparison?.length > 0 && (
              <div style={{ fontSize: 12, marginTop: 6 }}>
                {proposal.comparison.map((cmp, i) => (
                  <div key={i}>
                    For comparison, {cmp.source} ({cmp.period}):{" "}
                    {Object.entries(cmp.cases || {}).map(([k, v]) => `${k} ${fmt(v[0], 1)} / ${fmt(v[1], 1)} °C`)
                      .join(" · ")} (dry bulb / wet bulb).
                  </div>
                ))}
              </div>
            )}
            {proposal.era5?.message && (
              <div style={{ fontSize: 12, color: "#92400E", marginTop: 6, display: "flex", gap: 8,
                            alignItems: "center", flexWrap: "wrap" }}>
                <span>{proposal.era5.message}</span>
                {proposal.era5.status !== "UNAVAILABLE" && (
                  <button onClick={() => pick(proposal.place)} disabled={!!looking}
                    style={{ padding: "3px 10px", background: "#fff", color: KZ.blue,
                             border: `1px solid ${KZ.blue}`, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>
                    Check again
                  </button>
                )}
              </div>
            )}
            {proposal.licence?.text && (
              <div style={{ fontSize: 11, color: KZ.grey, marginTop: 4 }}>{proposal.licence.text}</div>
            )}
            {stations && stations.results?.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12.5 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Station route — {stations.label} (KAPL's secondary source; the nearest is proposed,
                  the Checker confirms the station on approval)
                </div>
                {stations.results.map((st, i) => (
                  <div key={st.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0" }}>
                    <span style={{ flex: 1 }}>
                      {i === 0 && <b>Proposed: </b>}{st.name} (ISD {st.id}) · {fmt(st.distance_km, 1)} km
                      {st.elevation_m != null && ` · ${fmt(st.elevation_m, 0)} m`}
                      {proposal.station?.id === st.id && <b style={{ color: KZ.green }}> · shown above</b>}
                    </span>
                    <button onClick={() => fromStation(st)} disabled={!!looking}
                      style={{ padding: "3px 10px", background: "#fff", color: KZ.blue,
                               border: `1px solid ${KZ.blue}`, borderRadius: 6, cursor: "pointer", fontSize: 12 }}>
                      Calculate from this station
                    </button>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: KZ.grey, marginTop: 3 }}>{stations.attribution}</div>
              </div>
            )}
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
              <button onClick={apply}
                style={{ padding: "7px 16px", background: KZ.green, color: "#fff", border: 0,
                         borderRadius: 7, fontWeight: 600, cursor: "pointer" }}>
                Use these for this project
              </button>
              <button onClick={() => { setProposal(null); setStations(null); }}
                style={{ padding: "7px 14px", background: "#fff", color: KZ.grey,
                         border: "1px solid #E5E7EB", borderRadius: 7, cursor: "pointer" }}>
                Discard
              </button>
            </div>
          </div>
        )}

        {applied.length > 0 ? (
          <>
            <StatesTable cases={applied} editable={editable} onEdit={editCase} tierOf={tierOf} />
            {basis.climate?.licence?.text ? (
              <div style={{ fontSize: 11, color: KZ.grey, marginTop: 6 }}>{basis.climate.licence.text}</div>
            ) : /open-meteo/i.test(basis.climate?.source || "") && (
              <div style={{ fontSize: 12, color: "#92400E", marginTop: 6 }}>
                These values came through Open-Meteo, whose free service is for non-commercial use only — look the
                location up again so the design basis rests on licensed data.
              </div>
            )}
            <div style={{ fontSize: 11.5, color: KZ.grey, marginTop: 6 }}>
              Every cooling case is evaluated on every room and the governing one is named in
              Heat Load. Edit a dry bulb or RH to override a case — the wet bulb, dew point
              and enthalpy follow, and an approved basis goes back to awaiting approval.
              {sc.applies && <> On this VRF project the looked-up values are reanalysis-derived reference data — a cross-check
                only, never approved ASHRAE data (KAPL C3, direction 6): the design ambient is the customer / tender value,
                then the KAPL-approved project basis, then the published ASHRAE 0.4 % DB with its mean coincident WB
                (entered manually with station, elevation, edition, percentile and source). State the governing case with
                its source in the heat balance inputs below.</>}
            </div>
            {editable && status === "FETCHED" && (
              <button onClick={approve}
                style={{ marginTop: 10, padding: "7px 16px", background: KZ.blue, color: "#fff",
                         border: 0, borderRadius: 7, fontWeight: 600, cursor: "pointer" }}>
                Approve these as the design basis
              </button>
            )}
          </>
        ) : !proposal && (
          <div style={{ fontSize: 12.5, color: "#92400E", background: "#FFFBEB",
                        border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 11px" }}>
            {sc.applies
              ? <>No outdoor case on the design basis — a VRF project states its design weather in the heat balance
                  inputs below. Look up the location for the reference.</>
              : <>No location yet — loads run on the budgetary {fmt(basis.outdoor_db_c)} °C /
                  {" "}{fmt((basis.outdoor_rh || 0) * 100, 0)}% outdoor condition, which the Design
                  Constants Master allows for budgeting only.</>}
          </div>
        )}
      </Section>

      {/* ── Indoor design condition ─────────────────────────────────────── */}
      {B("indoor_db_c") && <Section title="Indoor design condition"
        sub="Where a room states its own temperature and RH, the room's governs">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 12 }}>
          <div>
            <label style={lbl}>Dry bulb °C</label>
            {bandFor(ranges.rows, "indoor_db_c", "PROJECT", null)
              ? <RangeInput band={bandFor(ranges.rows, "indoor_db_c", "PROJECT", null)} value={val("indoor_db_c")}
                  disabled={!editable} onChange={v => setEdit(s => ({ ...s, indoor_db_c: v }))} />
              : <input style={editable ? inp : inpLocked} disabled={!editable} type="number" step="0.1"
                  value={val("indoor_db_c")} onChange={e => setEdit(s => ({ ...s, indoor_db_c: e.target.value }))} />}
          </div>
          <div>
            <label style={lbl}>RH %</label>
            <RangeInput band={bandFor(ranges.rows, "indoor_rh", "PROJECT", null) || { min: 0, max: 1, step: 0.01 }}
              scale={100} disabled={!editable}
              value={edit.indoor_rh !== undefined ? edit.indoor_rh
                     : (basis.indoor_rh != null ? +(basis.indoor_rh * 100).toFixed(1) : "")}
              onChange={v => setEdit(s => ({ ...s, indoor_rh: v }))} />
          </div>
          {[["Wet bulb °C", basis.indoor_state?.wb_c], ["Dew point °C", basis.indoor_state?.dew_point_c],
            ["Moisture g/kg", basis.indoor_state?.w_g_per_kg, 2],
            ["Enthalpy kJ/kg", basis.indoor_state?.enthalpy_kj_kg]].map(([l, v, d]) => (
            <div key={l}>
              <label style={lbl}>{l}</label>
              <div style={{ padding: "7px 0", fontSize: 13, fontWeight: 600 }}>{fmt(v, d || 1)}</div>
            </div>
          ))}
        </div>
      </Section>}

      {/* ── Every other global input ────────────────────────────────────── */}
      {groups.map(([group, fields]) => (
        <Section key={group} title={group}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 12 }}>
            {fields.map(([k, label, unit, hint]) => (
              <div key={k}>
                <label style={lbl} title={sc.help("basis", k) || undefined}>
                  {label}{sc.required("basis", k) ? " *" : ""}<ScopeMark sc={sc} rec="basis" k={k} />
                  <span style={{ marginLeft: 6, fontWeight: 600, textTransform: "none",
                                 color: stated.has(k) ? KZ.green : "#D1D5DB" }}>
                    {stated.has(k) ? "stated"
                      : (k === "air_density" && basis.derived_air_density ? "derived" : "default")}
                  </span>
                </label>
                {k === "heat_load_method" ? (
                  <select style={editable ? inp : inpLocked} disabled={!editable}
                    value={val(k) || "QUICK"} onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))}>
                    <option value="QUICK">QUICK — W/m² allowance</option>
                    <option value="DETAILED">DETAILED — room envelope</option>
                  </select>
                ) : k === "hepa_airflow_mode" ? (
                  <select style={editable ? inp : inpLocked} disabled={!editable}
                    value={val(k) || "COVERAGE_VELOCITY"} onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))}>
                    <option value="COVERAGE_VELOCITY">B — coverage + face velocity</option>
                    <option value="COVERAGE_ONLY">A — coverage only</option>
                  </select>
                ) : k === "envelope_include_roof_floor" ? (
                  <select style={editable ? inp : inpLocked} disabled={!editable}
                    value={edit[k] !== undefined ? edit[k] : (basis[k] ? "yes" : "no")}
                    onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))}>
                    <option value="no">No — wall and glass only</option>
                    <option value="yes">Yes — approved override</option>
                  </select>
                ) : bandFor(ranges.rows, k, "PROJECT", null) ? (
                  <RangeInput band={bandFor(ranges.rows, k, "PROJECT", null)} value={val(k)} disabled={!editable}
                    derived={k === "air_density" && basis.derived_air_density
                      ? +basis.derived_air_density.kg_m3.toFixed(3) : undefined}
                    onChange={v => setEdit(s => ({ ...s, [k]: v }))} />
                ) : (
                  <input style={editable ? inp : inpLocked} disabled={!editable} type="number" step="any"
                    title={lockedWhy || undefined}
                    placeholder={basis[k] == null ? (hint && hint.startsWith("Blank") ? "calculated / default" : "") : undefined}
                    value={val(k)} onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))} />
                )}
                <div style={{ fontSize: 10.5, color: "#9CA3AF", marginTop: 2 }}>
                  {unit}{hint ? ` · ${hint}` : ""}
                </div>
                {k === "air_density" && basis.air_density == null && basis.derived_air_density && (
                  <div style={{ fontSize: 11, color: "#1F4E79", marginTop: 3 }}>
                    Derived → <b>{fmt(basis.derived_air_density.kg_m3, 3)} kg/m³</b> at the indoor{" "}
                    {fmt(basis.derived_air_density.db_c, 1)} °C / {fmt(basis.derived_air_density.rh * 100, 0)} % and{" "}
                    {fmt(basis.derived_air_density.p_kpa, 2)} kPa ({fmt(basis.derived_air_density.altitude_m ?? 0, 0)} m
                    {basis.derived_air_density.from_lookup ? ", site elevation from the weather lookup" : ", altitude not looked up yet"});
                    each room uses its own conditions.
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      ))}

      <div>
        <label style={lbl}>Where these came from<ScopeMark sc={sc} rec="basis" k="source" /></label>
        <input style={editable ? inp : inpLocked} disabled={!editable}
          placeholder="a weather station, the customer's URS, the tender…"
          value={edit.source !== undefined ? edit.source : (basis.source || "")}
          onChange={e => setEdit(s => ({ ...s, source: e.target.value }))} />
      </div>

      {editable && (
        <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center",
                      position: "sticky", bottom: 0, background: "#fff", padding: "8px 0" }}>
          <button onClick={saveGlobals} disabled={!Object.keys(edit).length}
            style={{ padding: "9px 18px", background: Object.keys(edit).length ? KZ.blue : "#E5E7EB",
                     color: Object.keys(edit).length ? "#fff" : "#9CA3AF", border: "none",
                     borderRadius: 8, fontSize: 13, fontWeight: 700,
                     cursor: Object.keys(edit).length ? "pointer" : "not-allowed" }}>
            Save the design basis{Object.keys(edit).length ? ` (${Object.keys(edit).length})` : ""}
          </button>
          <span style={{ fontSize: 11.5, color: KZ.grey }}>
            Every load on this project is recomputed against it.
          </span>
        </div>
      )}
    </>
  );
}

/** The spec's 02_ROOM_INPUTS, column for column.
 *
 *  [input key, header, unit, save field, to-stored, from-stored, decimals]
 *  Airflows are entered in CFM and loads in kW as the sheet has them; the
 *  platform stores m³/h and W, and converts here — once, in one place. */
const CFM = 1.699;   // m³/h per CFM — K.CFM_PER_M3H
//  A tenth element `true` marks a column shown only where the project's input
//  matrix asks for it (length and width on a VRV / VRF project — the
//  cleanroom room master states area instead).
const ROOM_GROUPS = [
  ["Conditions", [
    ["length_m", "Length", "m", "length_m", null, null, 2, false, null, true],
    ["width_m", "Width", "m", "width_m", null, null, 2, false, null, true],
    ["cleanroom_class", "ISO class", "", "cleanroom_class", null, null, null, true],
    ["gmp_grade", "GMP grade", "A–D", "gmp_grade", null, null, null, true],
    ["area_m2", "Area", "m²", "area_m2"],
    ["height_m", "Ceiling ht", "m", "height_m"],
    ["temperature_c", "Room temp", "°C", "temperature_c"],
    ["rh_percent", "RH", "%", "rh_percent"],
    ["pressure_pa", "Pressure", "Pa", "pressure_pa"],
  ]],
  // KAPL 03-Oct-2026: the airflow pattern selects the primary method (Airflow
  // Engine §7); design ACH and coverage are the mean of min and max (CFM-002/3);
  // the filter is a KAPL catalogue code; the HEPA mode overrides the project's.
  ["Air", [
    ["airflow_pattern", "Pattern", "", "airflow_pattern", null, null, null, true, ["NON_UDAF", "UDAF"]],
    ["ach", "Min ACH", "/h", "ach"],
    ["ach_max", "Max ACH", "/h", "ach_max"],
    ["filter_coverage_pct", "Min coverage", "0–1", "filter_coverage_pct", null, null, 2],
    ["filter_coverage_max_pct", "Max coverage", "0–1", "filter_coverage_max_pct", null, null, 2],
    ["hepa_face_velocity_ms", "Face vel", "m/s", "hepa_face_velocity_ms", null, null, 2],
    ["hepa_model", "Filter", "catalogue", "hepa_model", null, null, null, true, "FILTERS"],
    ["hepa_mode", "HEPA mode", "", "hepa_mode", null, null, null, true, ["COVERAGE_VELOCITY", "COVERAGE_ONLY"]],
    ["fresh_air_ach", "Fresh air ACH", "/h", "fresh_air_ach"],
    ["exhaust_cmh", "Process exhaust", "CFM", "exhaust_cmh", v => v * CFM, v => v / CFM, 0],
    ["pressure_makeup_cmh", "Pressure makeup", "CFM", "pressure_makeup_cmh", v => v * CFM, v => v / CFM, 0],
  ]],
  ["Internal loads", [
    ["equipment_w", "Connected equip", "kW", "equipment_load_w", v => v * 1000, v => v / 1000, 2],
    ["equipment_diversity", "Diversity", "0–1", "equipment_diversity", null, null, 2],
    ["equipment_heat_release", "Heat release", "0–1", "equipment_heat_release", null, null, 2],
    ["occupancy", "People", "", "occupancy", null, null, 0],
    ["process_w", "Extra process sens", "kW", "process_load_w", v => v * 1000, v => v / 1000, 2],
    ["process_latent_w", "Extra process lat", "kW", "process_latent_w", v => v * 1000, v => v / 1000, 2],
    ["process_moisture_kgh", "Process moisture", "kg/h", "process_moisture_kgh", null, null, 2],
  ]],
  ["Envelope (DETAILED)", [
    ["ext_wall_area_m2", "Ext wall", "m²", "ext_wall_area_m2"],
    ["roof_area_m2", "Roof", "m²", "roof_area_m2"],
    ["glass_area_m2", "Glass", "m²", "glass_area_m2"],
    ["floor_area_m2", "Floor", "m²", "floor_area_m2"],
    ["wall_u", "Wall U", "W/m²K", "wall_u", null, null, 2],
    ["roof_u", "Roof U", "W/m²K", "roof_u", null, null, 2],
    ["glass_u", "Glass U", "W/m²K", "glass_u", null, null, 2],
    ["floor_u", "Floor U", "W/m²K", "floor_u", null, null, 2],
    ["solar_w_m2", "Solar", "W/m²", "solar_w_m2", null, null, 0],
    ["shgc", "SHGC", "0–1", "shgc", null, null, 2],
  ]],
];

/** A ranged room cell: number box, and a slider held inside the room's band
 *  while the cell is being edited (parameter ranges, KAPL 11-Oct-2026). */
function RangeCell({ band, shown, onCommit }) {
  const [v, setV] = React.useState(shown);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setV(shown); }, [shown]);
  const commit = (x) => { if (String(x) !== String(shown)) onCommit(x); };
  const off = outside(v, band);
  return (
    <div style={{ position: "relative" }}
      onFocus={() => setOpen(true)}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) { setOpen(false); commit(v); } }}>
      <input type="number" step="any" value={v} onChange={e => setV(e.target.value)}
        onKeyDown={e => e.key === "Enter" && e.target.blur()}
        title={`Allowed ${band.min}–${band.max}${band.variant ? " for " + band.variant : ""} (${band.status}) — ${band.source || ""}`}
        style={{ width: 68, padding: "4px 6px", fontSize: 12.5, borderRadius: 5,
                 border: `1px solid ${off ? "#DC2626" : "#E5E7EB"}`, background: off ? "#FEF2F2" : (v === "" ? "#FAFAFA" : "#fff") }} />
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 5, background: "#fff", width: 220,
                      border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", boxShadow: "0 4px 12px rgba(0,0,0,.08)" }}>
          <RangeInput band={band} value={v} onChange={setV} />
        </div>)}
    </div>
  );
}

export function RoomInputsGrid({ rooms, editable, onSave, method, filters, ranges, scope }) {
  // The project's input matrix decides the columns (KAPL ENG VRF IN 001 on a
  // VRV / VRF project: identity, L × W × H, indoor DB / RH; area derived).
  // No matrix: every column of the spec's room inputs, as before.
  const sc = scope && scope.applies ? scope : null;
  const showCol = (c) => (sc ? sc.show("room", c[3]) : !c[9]);
  const groups = ROOM_GROUPS.map(([g, c]) => [g, c.filter(showCol)]).filter(([, c]) => c.length);
  if (!rooms.length) {
    return (
      <div style={{ fontSize: 12.5, color: "#92400E", background: "#FFFBEB",
                    border: "1px solid #FDE68A", borderRadius: 8, padding: "9px 12px" }}>
        No rooms on this project yet. They come from the Room Master — add them there and
        every step after this one fills itself in.
      </div>
    );
  }
  const cols = groups.flatMap(([, c]) => c);
  return (
    <>
      {sc ? (
        <ScopeNote sc={sc}>Area is calculated from length × width. Each room's VRF inputs — application,
          hours, RH control, people, lighting, equipment, envelope, air streams — are on stage 4.</ScopeNote>
      ) : (
      <div style={{ fontSize: 12, color: KZ.grey, marginBottom: 8 }}>
        Every column of the spec's room inputs. Click a cell to type; it saves when you leave it
        and every later step recomputes. Blank means "not stated" — the default applies.
        {method !== "DETAILED" && " The envelope columns are used when the design basis is set to DETAILED."}
      </div>)}
      <div style={{ overflowX: "auto", maxHeight: "60vh", border: "1px solid #E5E7EB", borderRadius: 8 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12.5 }}>
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
            <tr>
              <th style={{ ...th, position: "sticky", left: 0, background: "#fff", zIndex: 3 }} rowSpan={2}>Room</th>
              <th style={th} rowSpan={2}>RAHU / zone</th>
              {groups.map(([g, c]) => (
                <th key={g} colSpan={c.length}
                  style={{ ...th, background: "#F9FAFB", color: KZ.charcoal }}>{g}</th>
              ))}
              <th style={th} rowSpan={2}>Volume m³</th>
            </tr>
            <tr>
              {cols.map(([k, h, u, field]) => (
                <th key={k} style={th} title={sc ? sc.help("room", field) || undefined : undefined}>
                  {h}{sc && sc.required("room", field) ? " *" : ""}
                  {u && <span style={{ fontWeight: 500, color: "#9CA3AF" }}> {u}</span>}
                  {sc && <ScopeMark sc={sc} rec="room" k={field} />}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rooms.map(r => {
              const inpts = r.inputs || r;
              return (
                <tr key={r.room_id || r.room}>
                  <td style={{ ...td, fontWeight: 600, position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                    {r.room_no ? `${r.room_no} · ` : ""}{r.room || "—"}
                  </td>
                  <td style={{ ...td, padding: editable && r.room_id ? "2px 4px" : td.padding, color: KZ.grey }}>
                    {editable && r.room_id ? (
                      <input key={`${r.room_id}-zone-${r.zone}`} defaultValue={r.zone || ""}
                        title="The air handler / system serving this room. Type an existing zone or a new one."
                        onBlur={e => { const v = e.target.value.trim(); if (v && v !== (r.zone || "")) onSave(r.room_id, "zone_name", v); }}
                        onKeyDown={e => e.key === "Enter" && e.target.blur()}
                        style={{ width: 80, padding: "4px 6px", fontSize: 12.5, border: "1px solid #E5E7EB", borderRadius: 5 }} />
                    ) : (r.zone || "—")}
                  </td>
                  {cols.map(([k, , , field, toStored, fromStored, dp, isText, choices]) => {
                    const raw = inpts[k];
                    const shown = raw === null || raw === undefined ? ""
                      : isText ? raw
                      : +(fromStored ? fromStored(Number(raw)) : Number(raw)).toFixed(dp ?? 2);
                    if (!editable || !r.room_id || (sc && !sc.editable("room", field))) {
                      const ob = !isText && ranges ? bandFor(ranges, k, "ROOM", inpts) : null;
                      return <td key={k} style={{ ...td, color: outside(shown, ob) ? "#DC2626" : undefined }}
                                 title={ob ? `Allowed ${ob.min}–${ob.max}` : undefined}>{shown === "" ? "—" : shown}</td>;
                    }
                    const rb = !isText && ranges ? bandFor(ranges, k, "ROOM", inpts) : null;
                    if (rb) {
                      return (
                        <td key={k} style={{ ...td, padding: "2px 4px" }}>
                          <RangeCell band={rb} shown={shown}
                            onCommit={v => onSave(r.room_id, field, v === "" ? "" : String(toStored ? toStored(Number(v)) : Number(v)))} />
                        </td>
                      );
                    }
                    if (choices) {
                      const opts = choices === "FILTERS"
                        ? (filters || []).map(f => [f.code, `${f.code}${f.ceiling_module ? "" : " (not a ceiling module)"}`])
                        : choices.map(c => [c, c.replace(/_/g, " ").toLowerCase()]);
                      return (
                        <td key={k} style={{ ...td, padding: "2px 4px" }}>
                          <select value={shown} onChange={e => onSave(r.room_id, field, e.target.value)}
                            style={{ padding: "3px 4px", fontSize: 12, border: "1px solid #E5E7EB", borderRadius: 5,
                                     maxWidth: choices === "FILTERS" ? 190 : 140,
                                     background: shown === "" ? "#FAFAFA" : "#fff" }}>
                            <option value="">{k === "hepa_mode" ? "project's" : k === "hepa_model" ? "default" : "—"}</option>
                            {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                      );
                    }
                    return (
                      <td key={k} style={{ ...td, padding: "2px 4px" }}>
                        <input key={`${r.room_id}-${k}-${shown}`} defaultValue={shown}
                          type={isText ? "text" : "number"} step="any"
                          onBlur={e => {
                            const v = e.target.value;
                            if (String(v) === String(shown)) return;
                            const out = v === "" ? "" : isText ? v
                              : String(toStored ? toStored(Number(v)) : Number(v));
                            onSave(r.room_id, field, out);
                          }}
                          onKeyDown={e => e.key === "Enter" && e.target.blur()}
                          style={{ width: isText ? 72 : 68, padding: "4px 6px", fontSize: 12.5,
                                   border: "1px solid #E5E7EB", borderRadius: 5,
                                   background: shown === "" ? "#FAFAFA" : "#fff" }} />
                      </td>
                    );
                  })}
                  {/* Calculated, so never a box — see test_derived_not_editable. */}
                  <td style={{ ...td, color: KZ.grey }}
                    title="Calculated: area × height">{fmt(r.volume_m3, 1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Calculation runs — Implementation Blueprint §2 / §4.
 *  A run is the result together with every revision it was computed from.
 *  Recording never touches an earlier run; release freezes one; a changed
 *  source marks it stale and says which. */
export function CalcRuns({ api, authFetch, projectId, editable, me, onChanged }) {
  const [runs, setRuns] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [err, setErr] = React.useState("");
  const load = React.useCallback(() => {
    if (!projectId) return;
    authFetch(`${api}/api/eng/projects/${projectId}/runs`)
      .then(r => r.json()).then(d => setRuns(d.runs || [])).catch(e => setErr(String(e.message || e)));
  }, [api, authFetch, projectId]);
  React.useEffect(() => { load(); }, [load]);
  const post = async (url, body, what) => {
    setBusy(what); setErr("");
    try {
      const r = await authFetch(url, { method: "POST", headers: { "Content-Type": "application/json" },
                                       body: JSON.stringify(body || {}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Refused");
      load();
      onChanged && onChanged();
    } catch (e) { setErr(String(e.message || e)); }
    setBusy("");
  };
  const download = async (id) => {
    const r = await authFetch(`${api}/api/eng/runs/${id}`);
    const d = await r.json();
    const url = URL.createObjectURL(new Blob([JSON.stringify({ request: d.request, result: d.result,
      revisions: d.revisions, engine_version: d.engine_version, state: d.state }, null, 1)],
      { type: "application/json" }));
    const a = document.createElement("a"); a.href = url; a.download = `${id}.json`; a.click();
    URL.revokeObjectURL(url);
  };
  const tone = { PRELIMINARY: ["#EFF6FF", "#1D4ED8"], RELEASED: ["#ECFDF5", "#047857"],
                 SUPERSEDED: ["#F3F4F6", "#6B7280"] };
  const latest = (runs || [])[0];
  return (
    <Section title="Calculation runs"
      sub="Each run records the result with every revision it used (Blueprint §2) — recalculation never overwrites"
      right={editable && (
        <button onClick={() => post(`${api}/api/eng/projects/${projectId}/runs`, { discipline: "HVAC" }, "Recording…")}
          disabled={!!busy}
          style={{ padding: "6px 14px", background: KZ.blue, color: "#fff", border: 0, borderRadius: 7,
                   fontWeight: 600, cursor: "pointer" }}>{busy || "Record run"}</button>)}>
      {err && <div style={{ color: KZ.red, fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {latest && latest.stale && (
        <div style={{ fontSize: 12.5, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A",
                      borderRadius: 8, padding: "7px 11px", marginBottom: 8 }}>
          The latest run is stale — {latest.stale_because.join("; ")}. The figures below are the live
          calculation; record a new run to keep them.
        </div>
      )}
      {runs && runs.length === 0 && (
        <div style={{ fontSize: 12.5, color: KZ.grey }}>No run recorded yet. The figures below are the
          live calculation; a run keeps them with their revisions.</div>
      )}
      {runs && runs.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Run", "State", "Basis", "TR", "Recorded", "Released", "Sources", ""].map(h =>
            <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {runs.map(r => {
              const [bg, fg] = tone[r.state] || tone.SUPERSEDED;
              return (
                <tr key={r.run_id}>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{r.run_id}
                    <div style={{ color: KZ.grey, fontFamily: "inherit", fontSize: 11 }}>{r.engine_version}</div></td>
                  <td style={td}><span style={{ background: bg, color: fg, borderRadius: 999, padding: "2px 8px",
                                                fontSize: 11, fontWeight: 700 }}>{r.release_status || r.state}</span></td>
                  <td style={{ ...td, color: r.basis_class === "FIRM" ? KZ.green : "#92400E", fontWeight: 600 }}>
                    {r.basis_class}</td>
                  <td style={td}>{fmt(r.summary?.cooling_tr, 2)}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.created_by}<div style={{ color: KZ.grey }}>
                    {(r.created_at || "").slice(0, 16).replace("T", " ")} UTC</div></td>
                  <td style={{ ...td, fontSize: 12 }}>{r.released_by || "—"}</td>
                  <td style={{ ...td, whiteSpace: "normal", fontSize: 12,
                               color: r.stale ? "#92400E" : KZ.green, maxWidth: 280 }}>
                    {r.stale ? `Stale: ${r.stale_because.join("; ")}` : "Current"}</td>
                  <td style={{ ...td }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {editable && r.state === "PRELIMINARY" && !r.stale && r.created_by !== me && (
                        <button onClick={() => post(`${api}/api/eng/runs/${r.run_id}/release`,
                          { note: window.prompt("Release note (optional)") || null }, "Releasing…")}
                          style={{ padding: "3px 9px", background: KZ.green, color: "#fff", border: 0,
                                   borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>Release</button>
                      )}
                      <button onClick={() => download(r.run_id)}
                        style={{ padding: "3px 9px", background: "#fff", color: KZ.grey, border: "1px solid #E5E7EB",
                                 borderRadius: 6, fontSize: 11.5, cursor: "pointer" }}>JSON</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Section>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// The VRF engine's screens are in VrfEngine.jsx (KAPL ENG VRF 001 Rev 03).
// ════════════════════════════════════════════════════════════════════════════

/** A text input that shows what is being typed while it has focus, so "0."
 *  is not turned into "0" on the keystroke. */
export function DraftInput({ value, onText, ...rest }) {
  const [text, setText] = React.useState(null);
  return (
    <input {...rest} value={text !== null ? text : (value ?? "")}
      onFocus={() => setText(value === null || value === undefined ? "" : String(value))}
      onChange={e => { setText(e.target.value); onText(e.target.value); }}
      onBlur={() => setText(null)} />
  );
}

/** KAPL A2 / C3 / Rev 03 §5 design-ambient source tiers a person states on a
 *  case; the weather lookup is a reference only (the server refuses it as a
 *  stated tier). */
export const WEATHER_TIERS = [
  ["CUSTOMER_TENDER", "Customer / tender-approved"],
  ["KAPL_PROJECT_BASIS", "KAPL-approved project basis"],
  ["ASHRAE_0_4", "Approved ASHRAE 0.4 % (station data)"],
];
const TIER_LABEL = { ...Object.fromEntries(WEATHER_TIERS),
                     LOOKUP_COMPARISON: "Reanalysis-derived reference data — reference only" };
